import { Component } from '@theme/component';
import { fetchConfig, resetShimmer } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';
import { formatMoney } from '@theme/money-formatting';

/**
 * A custom element that renders a transit cover opt-in checkbox in the cart.
 * Adds or removes the appropriate transit cover variant based on cart total tier.
 *
 * Cart `cart:update` handling is deferred (requestAnimationFrame) so it runs after
 * `cart-items-component` morphs the header section; otherwise this instance's
 * document listener was removed before it ran, and tier swaps never executed.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} checkbox - The checkbox input
 */

/** @type {boolean} */
let transitCoverPostCartRafScheduled = false;

function queueAllTransitCoverSync() {
  if (transitCoverPostCartRafScheduled) return;
  transitCoverPostCartRafScheduled = true;
  requestAnimationFrame(() => {
    transitCoverPostCartRafScheduled = false;
    document.querySelectorAll('transit-cover-component').forEach((node) => {
      if (node instanceof TransitCoverComponent) {
        node.syncTierFromCart();
      }
    });
  });
}

class TransitCoverComponent extends Component {
  /** @type {boolean} */
  #isUpdating = false;

  /** @type {boolean} */
  #syncMutex = false;

  /**
   * Fetches /cart.js, updates the advertised tier price, data attributes, and swaps
   * the cart line to the correct variant when transit cover is selected.
   */
  syncTierFromCart() {
    if (this.#isUpdating || this.#syncMutex) return;
    this.#syncTierFromCartAsync();
  }

  async #syncTierFromCartAsync() {
    if (this.#syncMutex) return;
    this.#syncMutex = true;

    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Failed to fetch cart');
      const cart = await response.json();

      let activeVariantId = '';
      let coverLinePrice = 0;

      for (const item of cart.items ?? []) {
        if (item.handle === 'transit-cover') {
          activeVariantId = String(item.variant_id);
          coverLinePrice = item.line_price;
          break;
        }
      }

      const totalExcludingCover = (cart.total_price ?? 0) - coverLinePrice;
      this.dataset.cartTotalExcludingCover = String(totalExcludingCover);

      if (activeVariantId && totalExcludingCover <= 0) {
        this.dataset.activeVariantId = activeVariantId;
        await this.#removeCover();
        return;
      }

      const correctVariantId = this.#getVariantIdForTotal(totalExcludingCover);
      this.#updateAdvertisedPrice(correctVariantId);

      if (activeVariantId) {
        this.dataset.activeVariantId = activeVariantId;
        if (correctVariantId && correctVariantId !== activeVariantId) {
          await this.#swapVariant(activeVariantId, correctVariantId);
        }
      } else {
        this.dataset.activeVariantId = '';
      }
    } catch {
      // Non-fatal: tier hint may stay stale until the next cart interaction
    } finally {
      this.#syncMutex = false;
    }
  }

  get #sectionId() {
    return this.dataset.sectionId ?? '';
  }

  get #activeVariantId() {
    return this.dataset.activeVariantId ?? '';
  }

  get #moneyFormat() {
    return this.dataset.moneyFormat ?? '';
  }

  get #currency() {
    return this.dataset.currency ?? '';
  }

  /**
   * Parses the data-variants attribute into an array of { id, threshold, priceCents }.
   * Attribute format: "variantId:thresholdDollars:priceCents,..." (legacy two-part entries omit price).
   * @returns {{ id: string, threshold: number, priceCents: number }[]}
   */
  get #variantEntries() {
    return (this.dataset.variants ?? '')
      .split(',')
      .map((entry) => {
        const parts = entry.split(':');
        const id = parts[0] ?? '';
        const threshold = parseInt(parts[1] ?? '0', 10);
        const priceCents = parts.length >= 3 ? parseInt(parts[2] ?? '', 10) : Number.NaN;
        return { id, threshold, priceCents };
      })
      .filter((v) => v.id && Number.isFinite(v.threshold));
  }

  /**
   * @param {string} variantId
   */
  #updateAdvertisedPrice(variantId) {
    const priceEl = this.querySelector('.transit-cover__price');
    if (!(priceEl instanceof HTMLElement)) return;

    const entry = this.#variantEntries.find((v) => v.id === variantId);
    if (!entry || !Number.isFinite(entry.priceCents) || !this.#moneyFormat || !this.#currency) {
      return;
    }

    const formatted = formatMoney(entry.priceCents, this.#moneyFormat, this.#currency);
    priceEl.textContent = `(+${formatted})`;
    priceEl.removeAttribute('hidden');
  }

  /**
   * Returns the variant ID whose threshold is the highest value the given
   * cart total (in cents) meets or exceeds.
   * @param {number} totalCents
   * @returns {string}
   */
  #getVariantIdForTotal(totalCents) {
    let best = /** @type {{ id: string, threshold: number } | null} */ (null);
    for (const v of this.#variantEntries) {
      if (totalCents >= v.threshold * 100 && (!best || v.threshold > best.threshold)) {
        best = v;
      }
    }
    return best?.id ?? '';
  }

  /**
   * Returns the correct variant ID based on the current cart total data attribute.
   * @returns {string}
   */
  #getCurrentVariantId() {
    const totalCents = parseInt(this.dataset.cartTotalExcludingCover ?? '0', 10);
    return this.#getVariantIdForTotal(totalCents);
  }

  /**
   * Shimmer the cart total and disable the checkbox while a request is in flight.
   * @param {boolean} loading
   */
  #setLoading(loading) {
    const checkbox = this.refs.checkbox;
    if (checkbox instanceof HTMLInputElement) {
      checkbox.disabled = loading;
    }

    const cartTotal = /** @type {any} */ (document.querySelector('text-component[data-cart-subtotal]'));
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (loading) {
      cartTotal?.shimmer?.();
      cartItemsComponent?.classList.add('cart-items-disabled');
    } else {
      resetShimmer(document.body);
      cartItemsComponent?.classList.remove('cart-items-disabled');
    }
  }

  /**
   * Toggle transit cover on checkbox change.
   * Called via `on:change="/toggle"` on the checkbox element.
   */
  toggle = async () => {
    if (this.#isUpdating) return;
    const checkbox = this.refs.checkbox;
    if (!(checkbox instanceof HTMLInputElement)) return;

    if (checkbox.checked) {
      await this.#addCover();
    } else {
      await this.#removeCover();
    }
  };

  async #addCover() {
    const variantId = this.#getCurrentVariantId();
    if (!variantId) return;

    this.#isUpdating = true;
    this.#setLoading(true);
    try {
      const body = JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: this.#sectionId,
        sections_url: window.location.pathname,
      });

      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const data = await response.json();

      if (!response.ok || data.status) {
        const checkbox = this.refs.checkbox;
        if (checkbox instanceof HTMLInputElement) checkbox.checked = false;
        return;
      }

      if (this.#sectionId && data.sections?.[this.#sectionId]) {
        morphSection(this.#sectionId, data.sections[this.#sectionId]);
      }
    } catch {
      const checkbox = this.refs.checkbox;
      if (checkbox instanceof HTMLInputElement) checkbox.checked = false;
    } finally {
      this.#isUpdating = false;
      this.#setLoading(false);
    }
  }

  async #removeCover() {
    const variantId = this.#activeVariantId;
    if (!variantId) return;

    this.#isUpdating = true;
    this.#setLoading(true);
    try {
      const body = JSON.stringify({
        id: variantId,
        quantity: 0,
        sections: this.#sectionId,
        sections_url: window.location.pathname,
      });

      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
      if (!response.ok) {
        const checkbox = this.refs.checkbox;
        if (checkbox instanceof HTMLInputElement) checkbox.checked = true;
        return;
      }
      const data = await response.json();

      if (this.#sectionId && data.sections?.[this.#sectionId]) {
        morphSection(this.#sectionId, data.sections[this.#sectionId]);
      }

      document.dispatchEvent(
        new CartUpdateEvent(data, this.id, { itemCount: data.item_count, source: 'transit-cover-component' })
      );
    } catch {
      const checkbox = this.refs.checkbox;
      if (checkbox instanceof HTMLInputElement) checkbox.checked = true;
    } finally {
      this.#isUpdating = false;
      this.#setLoading(false);
    }
  }

  /**
   * Swaps from one transit cover variant to another when the tier changes.
   * @param {string} oldVariantId
   * @param {string} newVariantId
   */
  async #swapVariant(oldVariantId, newVariantId) {
    this.#isUpdating = true;
    this.#setLoading(true);
    try {
      const body = JSON.stringify({
        updates: {
          [oldVariantId]: 0,
          [newVariantId]: 1,
        },
        sections: this.#sectionId,
        sections_url: window.location.pathname,
      });

      const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
      if (!response.ok) return;
      const data = await response.json();

      if (this.#sectionId && data.sections?.[this.#sectionId]) {
        morphSection(this.#sectionId, data.sections[this.#sectionId]);
      }
    } catch {
    } finally {
      this.#isUpdating = false;
      this.#setLoading(false);
    }
  }
}

if (!customElements.get('transit-cover-component')) {
  customElements.define('transit-cover-component', TransitCoverComponent);
}

if (typeof window !== 'undefined' && !window.__transitCoverCartSyncInstalled) {
  window.__transitCoverCartSyncInstalled = true;
  document.addEventListener(ThemeEvents.cartUpdate, () => {
    queueAllTransitCoverSync();
  });
}
